import React, {
  cloneElement,
  FormEventHandler,
  FunctionComponent,
  ReactElement,
  useState,
} from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { appOverride } from '../../util/AppUtils';
import { AppIcons } from '../assets/images/Icons';
import {
  OptionButtonInput,
  OptionButtonLabel,
  OptionButtonProps,
  OptionButtonWidth,
  OptionFieldset,
} from '../styles/OptionButton';

// Note: keeping this prop type separate to Radio Button to allow future props such as indeterminate
type CheckboxProps = OptionButtonProps;

export const Checkbox: FunctionComponent<CheckboxProps> = (props) => {
  const {
    children,
    width = OptionButtonWidth.Wide,
    value,
    selected,
    animationEnabled,
    animationIndex,
  } = props;
  const inputId = (children as string).replace(/\s+/g, '');

  return (
    <CheckboxLabel
      className={selected ? 'selected' : undefined}
      width={width}
      htmlFor={inputId}
      data-test="optionButton"
      checkboxInnerIcon={AppIcons.tickIcon}
      animationEnabled={animationEnabled}
      animationIndex={animationIndex}
    >
      <OptionButtonInput type="checkbox" name="options" value={value} id={inputId} />
      {children}
    </CheckboxLabel>
  );
};

type CheckboxGroupProps = {
  defaultSelected: string[] | undefined;
  onChange: (changedValue: string[]) => void;
  /**
   * Values that can't be combined with anything else, e.g. "None". Picking one clears every other
   * selection, and picking any other value clears them — otherwise an answer like "None + IBS"
   * could be submitted.
   */
  exclusiveChoices?: string[];
  children: ReactElement<CheckboxProps>[];
};

export const CheckboxGroup: FunctionComponent<CheckboxGroupProps> = (props) => {
  const { defaultSelected = [], onChange, exclusiveChoices = [], children } = props;
  const [selected, setSelected] = useState(defaultSelected);

  function isSelected(option: string) {
    return selected.includes(option);
  }

  function applyExclusivity(newSelected: string[], changedValue: string) {
    if (exclusiveChoices.includes(changedValue)) return [changedValue];
    return newSelected.filter((item) => !exclusiveChoices.includes(item));
  }

  const onSelectedChange: FormEventHandler<HTMLFieldSetElement> = (event) => {
    const changedValue = (event.target as HTMLInputElement).value;

    const newSelected = isSelected(changedValue)
      ? selected.filter((item) => item !== changedValue)
      : applyExclusivity(selected.concat(changedValue), changedValue);

    onChange(newSelected);
    setSelected(newSelected);
  };

  return (
    <OptionFieldset onChange={onSelectedChange}>
      {children.map((child: JSX.Element) =>
        cloneElement(child, { selected: isSelected(child.props.value) })
      )}
    </OptionFieldset>
  );
};

const CheckboxLabel = styled(OptionButtonLabel)<{ checkboxInnerIcon: string }>`
  /* Checkbox */
  position: relative;
  padding-right: ${({ theme }) =>
    appOverride({ nerva: theme.spacing._16px, default: theme.spacing._46px })};
  padding-left: ${({ theme }) => theme.spacing._46px};

  &:before {
    content: '';
    position: absolute;
    ${({ theme }) =>
      appOverride({
        nerva: `left: ${theme.spacing._16px}`,
        default: `right: ${theme.spacing._20px}`,
      })};
    display: inline-block;
    height: ${({ theme }) => theme.spacing._20px};
    width: ${({ theme }) => theme.spacing._20px};
    border-radius: ${({ theme }) => appOverride({ nerva: theme.spacing._4px, default: '50%' })};
    border: 1px solid
      ${appOverride({ nerva: Color.content.default, default: Color.decorative.default })};
  }

  &.selected:before {
    background-image: url(${({ checkboxInnerIcon }) => checkboxInnerIcon});
    background-position: center;
    border: 1px solid ${appOverride({ nerva: Color.content.default, default: Color.state.select })};
  }
`;
